import Bowser from 'bowser';
import { mount } from '../dom-lifecycle';

// Miniatura pliku w wierszu uploadu (złoty kwadrat + biała ikona zdjęcia) — jak w Figmie.
const THUMB_SVG = `<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="42" height="42" rx="8" fill="#cfa34f"/><g stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"><rect x="10.5" y="12.5" width="21" height="17" rx="2.5"/><path d="M11 26l5-5 4 4 4-5 7 8"/></g><circle cx="16.5" cy="18" r="2" fill="#fff"/></svg>`;

// Kroki ładowania (pasek postępu + odliczanie) są KOSMETYCZNE — jak w żywcu. Pod spodem, przy
// wyborze pliku, leci realny odczyt paragonu (mechanizm 4'o: POST save_receipt → receiptUuid +
// billData), dzięki czemu modal na WYŚLIJ pokazuje odczytane dane. Stan "sukces" pojawia się po
// max(czas animacji, realny odczyt). Realny scan ustawia receiptUuid i uzupełnia pola z OCR.
const MAX_FILE_BYTES = 12 * 1024 * 1024;
const ACCEPT_EXT = ['jpg', 'jpeg', 'png', 'pdf'];
const PROGRESS_SECONDS = 4;
const ERROR_INVALID_FILE = 'Niewłaściwy format pliku lub jego wielkość. Dodaj ponownie.';

// Jedno „podejście" = jedno załadowanie strony. Backend po zmianie tej wartości zeruje licznik
// nieudanych prób, więc kolejne wejście na formularz nie dziedziczy prób sprzed kwadransa,
// a retry z modala (bez przeładowania) normalnie je nabija.
const FLOW_ID = window.crypto?.randomUUID?.() ?? `flow-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// Polska odmiana: 1 sekunda / 2-4 sekundy / 5+ sekund.
function polishSeconds(n) {
    if (n === 1) {
        return '1 sekunda';
    }
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        return `${n} sekundy`;
    }
    return `${n} sekund`;
}

function formatFileSize(bytes) {
    if (bytes < 1024 * 1024) {
        return `${Math.max(1, Math.round(bytes / 1024))} kb`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function isValidFile(file) {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    return ACCEPT_EXT.includes(ext) && file.size <= MAX_FILE_BYTES;
}

mount('[data-receipt-file-upload]', function () {
    const $container = $(this);
    const $input = $container.find('[data-ref=input]');
    const $body = $container.find('[data-ref=body]');
    const $scannerModal = $container.find('[data-ref=scanner-modal]');
    const $scannerContainer = $container.find('[data-ref=scanner-container]');
    const initialBody = $body.html();
    let timers = [];
    let uploadSeq = 0; // rozróżnia bieżący wybór pliku od nieaktualnych, wciąż trwających scanów

    const clearTimers = () => {
        timers.forEach((id) => { clearTimeout(id); clearInterval(id); });
        timers = [];
    };

    const setState = (stateClass) => {
        $container.removeClass('is-uploading is-success is-error').addClass('has-file');
        if (stateClass) {
            $container.addClass(stateClass);
        }
    };

    const renderUploading = (file, secondsLeft) => {
        setState('is-uploading');
        $body.html(`
            <span class="af-dropzone__file">
                <span class="af-dropzone__file-row">
                    <span class="af-dropzone__thumb">${THUMB_SVG}</span>
                    <span class="af-dropzone__file-info">
                        <span class="af-dropzone__file-name">${escapeHtml(file.name)}</span>
                        <span class="af-dropzone__file-meta">
                            <span>${formatFileSize(file.size)}</span>
                            <span class="af-dropzone__dot"></span>
                            <span data-ref="countdown">pozostało ${polishSeconds(secondsLeft)}</span>
                        </span>
                    </span>
                </span>
                <span class="af-dropzone__bar"><span class="af-dropzone__bar-fill" data-ref="bar"></span></span>
            </span>
        `);
    };

    const renderResult = (file, stateClass, message) => {
        setState(stateClass);
        $body.html(`
            <span class="af-dropzone__file af-dropzone__file--result">
                <span class="af-dropzone__file-row af-dropzone__file-row--center">
                    <span class="af-dropzone__thumb">${THUMB_SVG}</span>
                    <span class="af-dropzone__file-name">${escapeHtml(file.name)}</span>
                </span>
                <span class="af-dropzone__status">${escapeHtml(message)}</span>
            </span>
        `);
    };

    const resetBody = () => {
        clearTimers();
        $container.removeClass('has-file is-uploading is-success is-error');
        $container.find('[name=receiptUuid]').remove();
        $body.html(initialBody);
    };

    const getWebsocketUuid = () => fetch($input.data('ws-uuid-url'))
        .then((response) => response.json())
        .then((data) => data.uuid)
        .catch(() => {});

    // Realny odczyt paragonu (mechanizm 4'o). Zwraca znormalizowany wynik, nie rzuca wyjątkiem.
    const scanReceipt = async (file) => {
        const formData = new FormData();
        formData.set('file', file);
        formData.set('_token', $input.data('token'));
        formData.set('wsUuid', await getWebsocketUuid());

        try {
            const data = await $.ajax({
                type: 'POST',
                url: $input.data('url'),
                data: formData,
                processData: false,
                contentType: false,
                // Identyfikator podejścia — backend zeruje po nim licznik nieudanych prób, gdy user
                // wchodzi na formularz od nowa. Nagłówek, a nie pole formularza, bo ReceiptType
                // odrzuca nadmiarowe pola (throw_validation_exception).
                headers: { 'X-Paragon-Flow': FLOW_ID },
            });
            return { ok: true, data };
        } catch (xhr) {
            const body = xhr.responseJSON;
            const errors = body?.errors;
            // `error` to pojedynczy komunikat spoza walidacji formularza — m.in. blokada wgrania
            // dokładnie tego samego zdjęcia po odrzuconym odczycie.
            const message = errors
                ? Object.values(errors).flat().join(' ')
                : (body?.error ?? 'Wystąpił nieznany błąd, proszę spróbować jeszcze raz.');
            return { ok: false, message };
        }
    };

    const applySuccess = (file, data) => {
        $container.find('[name=receiptUuid]').remove();
        if (data && data.receiptUuid) {
            $container.append(`<input type="hidden" name="receiptUuid" value="${escapeHtml(data.receiptUuid)}">`);
        }
        // Uzupełnij pola formularza danymi z OCR (jeśli paragon czytelny).
        if (data && data.billData) {
            for (const name in data.billData) {
                $(`input[name="personal_data[${name}]"]`).val(data.billData[name]).trigger('input');
            }
        }
        renderResult(file, 'is-success', 'Plik został poprawnie dodany');
    };

    // Odczyt się nie udał, a user ma jeszcze prawo do poprawy zdjęcia: zdjęcie NIE wchodzi do
    // formularza (brak receiptUuid + czysty input), a modal wyjaśnia, co poprawić. Formularz
    // ręczny pokaże się dopiero, gdy backend przyzna prawo do ręcznego uzupełnienia
    // (manualAllowed) — wtedy idziemy zwykłą ścieżką applySuccess i WYŚLIJ.
    const applyScanFailure = (file, scanResult) => {
        $input.val('');
        $container.find('[name=receiptUuid]').remove();
        renderResult(file, 'is-error', 'Nie udało się odczytać dowodu zakupu');
        document.dispatchEvent(new CustomEvent('paragon:scan-failed', { detail: { variant: scanResult } }));
    };

    const applyError = (file, message) => {
        $input.val('');
        $container.find('[name=receiptUuid]').remove();
        renderResult(file, 'is-error', message || ERROR_INVALID_FILE);
    };

    // Kosmetyczny pasek + odliczanie (fake) równolegle z realnym scanem. Wynik pokazujemy po
    // obu: minimalnym czasie animacji ORAZ zakończeniu odczytu.
    const startUpload = (file) => {
        clearTimers();
        const seq = ++uploadSeq;
        let secondsLeft = PROGRESS_SECONDS;
        renderUploading(file, secondsLeft);

        const bar = $container.find('[data-ref=bar]')[0];
        if (bar) {
            // Start od 0% bez animacji, wymuszony reflow, dopiero potem animacja do 100% —
            // inaczej pasek skoczyłby od razu na koniec.
            bar.style.transition = 'none';
            bar.style.width = '0%';
            void bar.offsetWidth;
            bar.style.transition = `width ${PROGRESS_SECONDS}s linear`;
            bar.style.width = '100%';
        }

        const countdownIv = setInterval(() => {
            secondsLeft -= 1;
            const el = $container.find('[data-ref=countdown]')[0];
            if (el && secondsLeft > 0) {
                el.textContent = `pozostało ${polishSeconds(secondsLeft)}`;
            }
        }, 1000);
        timers.push(countdownIv);

        const minWait = new Promise((resolve) => {
            timers.push(setTimeout(resolve, PROGRESS_SECONDS * 1000));
        });

        Promise.all([minWait, scanReceipt(file)]).then(([, result]) => {
            if (seq !== uploadSeq) {
                return; // wybrano w międzyczasie inny plik — nie nadpisuj
            }
            clearInterval(countdownIv);
            if (!result.ok) {
                applyError(file, result.message);
                return;
            }
            if (!result.data?.receiptUuid) {
                applyScanFailure(file, result.data?.scanResult);
                return;
            }
            applySuccess(file, result.data);
        });
    };

    const handleFile = (file) => {
        $container.find('.file-error').remove();
        clearTimers();

        if (!file) {
            resetBody();
            return;
        }
        if (!isValidFile(file)) {
            // Niepoprawny plik nie może trafić do formularza — czyścimy input.
            $input.val('');
            uploadSeq++;
            renderResult(file, 'is-error', ERROR_INVALID_FILE);
            return;
        }
        startUpload(file);
    };

    $input.on('change', function () {
        handleFile(this.files[0]);
    });

    // Drag & drop — upuszczenie pliku ustawia input i uruchamia upload.
    $container.on('dragover dragenter', function (e) {
        e.preventDefault();
        e.stopPropagation();
        $container.addClass('is-dragging');
    });
    $container.on('dragleave dragend drop', function (e) {
        e.preventDefault();
        e.stopPropagation();
        $container.removeClass('is-dragging');
    });
    $container.on('drop', function (e) {
        const files = e.originalEvent?.dataTransfer?.files;
        if (files && files.length) {
            $input[0].files = files;
            $input.trigger('change');
        }
    });

    // Skaner mobilny (kamera) — na telefonie/tablecie klik otwiera modal ze skanerem paragonu.
    $input.on('click', function (e) {
        if (['mobile', 'tablet'].includes(Bowser.parse(navigator.userAgent).platform.type)) {
            e.preventDefault();
            $scannerModal.modal('show');
        }
    });

    $scannerModal.on('show.bs.modal', function () {
        const scannerUrl = $input.data('scanner-url');

        $scannerContainer.append(
            `<iframe width="100%" height="auto" style="width: 100%; height: 100%; min-height: 90dvh;" src="${scannerUrl}" allow="camera; microphone"></iframe>`
        );
    });

    $scannerModal.on('hide.bs.modal', function () {
        if (window.innerWidth < 992) {
            $scannerContainer.find('iframe').remove();
        }
    });

    window.addEventListener('message', function (event) {
        let scannerData;

        try {
            scannerData = JSON.parse(event.data);
        } catch (e) {
            scannerData = null;
        }

        if (scannerData && scannerData?.name === 'scannerImage') {
            let file = dataURLtoFile(scannerData.data, `receipt-${Date.now()}.png`);
            let container = new DataTransfer();
            container.items.add(file);
            $input.prop('files', container.files).trigger('change');
            $scannerModal.modal('hide');
        }
    });
});

function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
}
